import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { RefObject } from 'react';

type UseOverflowTabsOptions = {
  itemCount: number;
  activeIndex: number;
  containerRef: RefObject<HTMLElement | null>;
  measurementRef: RefObject<HTMLElement | null>;
  moreWidth: number;
  alwaysReserveMore?: boolean;
  minimumHiddenCount?: number;
  gap?: number;
};

const getVisibleIndices = (
  widths: number[],
  availableWidth: number,
  moreWidth: number,
  gap: number,
  alwaysReserveMore: boolean,
  minimumHiddenCount: number
) => {
  if (
    !widths.length ||
    (!alwaysReserveMore &&
      widths.reduce((total, width) => total + width, 0) + gap * (widths.length - 1) <=
        availableWidth)
  ) {
    return widths.map((_, index) => index);
  }

  const availableForTabs = Math.max(0, availableWidth - moreWidth - gap);
  let usedWidth = 0;
  let visibleCount = 0;

  for (const width of widths) {
    const nextWidth = usedWidth + width + (visibleCount > 0 ? gap : 0);
    if (nextWidth > availableForTabs) break;
    usedWidth = nextWidth;
    visibleCount += 1;
  }

  visibleCount = Math.min(visibleCount, Math.max(0, widths.length - minimumHiddenCount));
  return widths.slice(0, visibleCount).map((_, index) => index);
};

export const useOverflowTabs = ({
  itemCount,
  activeIndex,
  containerRef,
  measurementRef,
  moreWidth,
  alwaysReserveMore = false,
  minimumHiddenCount = 2,
  gap = 8,
}: UseOverflowTabsOptions) => {
  const [visibleIndices, setVisibleIndices] = useState<number[]>(() =>
    Array.from({ length: itemCount }, (_, index) => index)
  );
  const resizeFrame = useRef<number | null>(null);

  const measure = useCallback(() => {
    const container = containerRef.current;
    const measurementContainer = measurementRef.current;
    if (!container || !measurementContainer) return;

    const elements = Array.from(
      measurementContainer.querySelectorAll<HTMLElement>('[data-overflow-tab-index]')
    );
    const widths = elements.map((element) => element.offsetWidth);
    if (widths.length !== itemCount) return;

    const nextVisibleIndices = getVisibleIndices(
      widths,
      container.clientWidth,
      moreWidth,
      gap,
      alwaysReserveMore,
      minimumHiddenCount
    );
    setVisibleIndices((current) =>
      current.length === nextVisibleIndices.length &&
      current.every((index, position) => index === nextVisibleIndices[position])
        ? current
        : nextVisibleIndices
    );
  }, [
    alwaysReserveMore,
    containerRef,
    gap,
    itemCount,
    measurementRef,
    minimumHiddenCount,
    moreWidth,
  ]);

  useLayoutEffect(() => {
    measure();
  }, [measure]);

  useEffect(() => {
    const container = containerRef.current;
    const measurementContainer = measurementRef.current;
    if (!container || !measurementContainer || typeof ResizeObserver === 'undefined') return;

    const observer = new ResizeObserver(() => {
      if (resizeFrame.current !== null) cancelAnimationFrame(resizeFrame.current);
      resizeFrame.current = requestAnimationFrame(measure);
    });
    observer.observe(container);
    observer.observe(measurementContainer);
    if (container.parentElement) observer.observe(container.parentElement);
    window.addEventListener('resize', measure);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', measure);
      if (resizeFrame.current !== null) cancelAnimationFrame(resizeFrame.current);
    };
  }, [containerRef, measure, measurementRef]);

  const validVisibleIndices = visibleIndices.filter((index) => index < itemCount);
  const currentHiddenIndices = Array.from({ length: itemCount }, (_, index) => index).filter(
    (index) => !validVisibleIndices.includes(index)
  );
  const promotedIndex = currentHiddenIndices.includes(activeIndex) ? activeIndex : null;
  const displayedIndices = [...validVisibleIndices];
  if (promotedIndex !== null && currentHiddenIndices.includes(promotedIndex)) {
    const insertionIndex = Math.max(0, displayedIndices.length - 1);
    if (displayedIndices.length >= 2) displayedIndices.splice(insertionIndex, 1);
    displayedIndices.splice(insertionIndex, 0, promotedIndex);
  }

  return {
    displayedIndices,
    hiddenIndices: Array.from({ length: itemCount }, (_, index) => index).filter(
      (index) => !displayedIndices.includes(index)
    ),
    hasOverflow: currentHiddenIndices.length > 0,
    measure,
  };
};
